import { createSelector } from "reselect";
import { BettingState, VALIDATION_SEVERITIES, VALIDATION_TYPES, Validations } from "@ppb/betslip-core";
import { hasSpecialValidation, isTerritoryApplicableValidation } from "../../helpers/territory-rules";
import { getSportsbookBettingState } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getSportsbookBettingValidations } from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { AlertProps, AlertViewModel } from "@ppb/the-wall-common/types";
import { SportsbookBettingState } from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { getShowMaxPayoutNotification } from "../../store/state/betslip-module/betslip-module-selectors";
import { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import type { BetslipLocalization, DailyPayoutLimitCapability } from "../../contract";

import {
  getAboveMaxPayoutGroupErrorNotification,
  getAboveMaxPayoutGroupWarningNotification,
  getAboveMaxPayoutNotification,
  getAboveMaxStakeNotification,
  getBelowMinStakeNotification,
  getIncrementOutOfRangeNotification,
  getMaxPayoutNotification,
  isMaxPayoutNotificationSupported,
} from "../../helpers/notifier-helper";
import { asHostProvidedKey } from "../../helpers/i18n";

const VALIDATIONS_GROUPING = [[VALIDATION_TYPES.ABOVE_MAX_PAYOUT, VALIDATION_TYPES.ABOVE_MAX_STAKE]];

export enum NOTIFICATION_TYPES {
  MAX_PAYOUT = "MAX_PAYOUT",
}

export type SportsbookValidation = {
  type: VALIDATION_TYPES | NOTIFICATION_TYPES;
  notification: AlertViewModel | null;
};

type CombinationValidation = {
  id: string;
  validation: Validations.CombinationsValidations;
};

type ValidationsByTypeMap = {
  [k in VALIDATION_TYPES]?: CombinationValidation;
};

export type NotifierContract = {
  localization: BetslipLocalization;
  maxPayoutTermsUrl: string;
  dailyPayoutLimit: DailyPayoutLimitCapability;
};

export const createNotificationFromCombinationValidation = (
  validation: Validations.CombinationsValidations,
  localization: BetslipLocalization,
  combination: BettingState.Combination,
): AlertProps | null => {
  switch (validation.type) {
    case VALIDATION_TYPES.BELOW_MIN_STAKE: {
      return getBelowMinStakeNotification(validation.data.min, localization);
    }
    case VALIDATION_TYPES.ABOVE_MAX_STAKE: {
      return getAboveMaxStakeNotification(validation.data.max, localization);
    }
    case VALIDATION_TYPES.ABOVE_MAX_PAYOUT: {
      return getAboveMaxPayoutNotification(validation.data.max, localization);
    }
    case VALIDATION_TYPES.INCREMENT_OUT_OF_RANGE: {
      return getIncrementOutOfRangeNotification(combination.minStakeIncrement, localization);
    }
    default:
      return null;
  }
};

export const createNotificationFromGroupValidation = (
  validation: Validations.CombinationsValidations,
  localization: BetslipLocalization,
  maxPayoutTermsUrl: string,
  dailyPayoutLimit: DailyPayoutLimitCapability,
): AlertProps | null => {
  const maxPayoutKey = dailyPayoutLimit.enabled ? asHostProvidedKey(dailyPayoutLimit.maxPayoutDisplayKey) : undefined;

  switch (validation.type) {
    case VALIDATION_TYPES.ABOVE_MAX_PAYOUT: {
      if (validation.severity === VALIDATION_SEVERITIES.ERROR && maxPayoutKey) {
        return getAboveMaxPayoutGroupErrorNotification(localization, maxPayoutKey);
      }

      if (validation.severity === VALIDATION_SEVERITIES.WARNING && maxPayoutKey) {
        return getAboveMaxPayoutGroupWarningNotification(localization, maxPayoutKey, maxPayoutTermsUrl);
      }

      return null;
    }
    default:
      return null;
  }
};

export const buildSportsbookValidation = (
  combinationValidation: CombinationValidation,
  notification: AlertViewModel | null,
): SportsbookValidation => {
  if (!notification?.items) {
    return {
      type: combinationValidation.validation.type,
      notification: null,
    };
  }

  return {
    type: combinationValidation.validation.type,
    notification: {
      id: combinationValidation.id,
      ...notification,
    },
  };
};

export const mergeSportsbookValidation = (
  validationA: SportsbookValidation,
  validationB: SportsbookValidation,
): SportsbookValidation => {
  const { notification: notificationA } = validationA;
  const { notification: notificationB } = validationB;

  if (!notificationA || !notificationB) {
    return {
      ...validationA,
      ...validationB,
      notification: null,
    };
  }

  return {
    ...validationA,
    ...validationB,
    notification: {
      ...notificationA,
      ...notificationB,
      items: notificationA.items && notificationB.items && [...notificationA.items, ...notificationB.items],
    },
  };
};

const groupCombinationValidations = (combinationValidations: ValidationsByTypeMap): CombinationValidation[][] => {
  const localCombinationValidations = { ...combinationValidations };
  const groupedValidations = VALIDATIONS_GROUPING.map((validationGroup) =>
    validationGroup
      .map((type) => {
        const validation = localCombinationValidations[type];

        delete localCombinationValidations[type]; // eslint-disable-line @typescript-eslint/no-dynamic-delete

        return validation;
      })
      .filter((validation): validation is CombinationValidation => !!validation),
  );

  return [...groupedValidations, ...Object.values(localCombinationValidations).map((validation) => [validation])];
};

const buildUniqueCombinationValidationsByTypeMap = (
  combinationsValidations: BettingState.CombinationsValidationsMap,
  localization: BetslipLocalization,
): ValidationsByTypeMap =>
  Object.keys(combinationsValidations).reduce<ValidationsByTypeMap>((acc, combinationId) => {
    const currentCombinationValidations = combinationsValidations[combinationId];
    currentCombinationValidations.forEach((validation) => {
      if (isTerritoryApplicableValidation(validation.type, localization)) {
        acc[validation.type] = { id: combinationId, validation };
      }
    });
    return acc;
  }, {});

const buildGroupValidations = (
  coreGroupValidations: Validations.GroupsValidations[],
  localization: BetslipLocalization,
  maxPayoutTermsUrl: string,
  hasAboveMaxStakeValidation: boolean,
  dailyPayoutLimit: DailyPayoutLimitCapability,
): SportsbookValidation[] =>
  coreGroupValidations.reduce<SportsbookValidation[]>((allSportsbookValidations, currentGroupValidation) => {
    if (!hasSpecialValidation(currentGroupValidation.type, localization, hasAboveMaxStakeValidation)) {
      return allSportsbookValidations;
    }
    const sportsbookValidation = {
      type: currentGroupValidation.type,
      notification: createNotificationFromGroupValidation(
        currentGroupValidation,
        localization,
        maxPayoutTermsUrl,
        dailyPayoutLimit,
      ),
    };
    allSportsbookValidations.push(sportsbookValidation);
    return allSportsbookValidations;
  }, []);

const flattenValidationGroup = (
  validationGroups: CombinationValidation[],
  localization: BetslipLocalization,
  sportsbookBettingState: SportsbookBettingState,
): SportsbookValidation => {
  const [firstCombinationValidation, ...remainingCombinationValidations] = validationGroups;
  const firstCombination = sportsbookBettingState.combinations[firstCombinationValidation.id];
  const firstNotification = createNotificationFromCombinationValidation(
    firstCombinationValidation.validation,
    localization,
    firstCombination,
  );

  const flattenedValidation = remainingCombinationValidations.reduce<SportsbookValidation>(
    (flatValidation, validationGroup) => {
      const combination = sportsbookBettingState.combinations[validationGroup.id];
      const notification = createNotificationFromCombinationValidation(
        validationGroup.validation,
        localization,
        combination,
      );
      const sportsbookValidation = buildSportsbookValidation(validationGroup, notification);

      return mergeSportsbookValidation(flatValidation, sportsbookValidation);
    },
    buildSportsbookValidation(firstCombinationValidation, firstNotification),
  );

  return flattenedValidation;
};

export const createValidationsSelector = () =>
  createSelector(
    [
      getSportsbookBettingValidations,
      getSportsbookBettingState,
      getShowMaxPayoutNotification,
      (_state: ApplicationState, contract: NotifierContract) => contract.localization,
      (_state: ApplicationState, contract: NotifierContract) => contract.maxPayoutTermsUrl,
      (_state: ApplicationState, contract: NotifierContract) => contract.dailyPayoutLimit,
    ],
    (
      coreValidations,
      sportsbookBettingState,
      isMaxPayoutNotificationVisible,
      localization,
      maxPayoutTermsUrl,
      dailyPayoutLimit,
    ) => {
      if (!localization.jurisdiction) {
        return [];
      }

      const uniqueCombinationValidations = buildUniqueCombinationValidationsByTypeMap(
        coreValidations.combinations,
        localization,
      );

      const groupedCombinationValidation = groupCombinationValidations(uniqueCombinationValidations);
      const sportsbookCombinationValidations = groupedCombinationValidation.reduce<SportsbookValidation[]>(
        (allFlattenedCombinationValidations, validationGroups) => {
          if (validationGroups.length) {
            allFlattenedCombinationValidations.push(
              flattenValidationGroup(validationGroups, localization, sportsbookBettingState),
            );
          }
          return allFlattenedCombinationValidations;
        },
        [],
      );

      const hasAboveMaxStakeValidation = sportsbookCombinationValidations.some(
        (validation) => validation.type === VALIDATION_TYPES.ABOVE_MAX_STAKE,
      );

      const sportsbookGroupValidations = buildGroupValidations(
        coreValidations.group,
        localization,
        maxPayoutTermsUrl,
        hasAboveMaxStakeValidation,
        dailyPayoutLimit,
      );

      const showMaxPayoutNotification =
        isMaxPayoutNotificationVisible && isMaxPayoutNotificationSupported(localization);
      const maxPayoutNotification = showMaxPayoutNotification
        ? [
            {
              type: NOTIFICATION_TYPES.MAX_PAYOUT,
              notification: getMaxPayoutNotification(maxPayoutTermsUrl),
            },
          ]
        : [];

      // Uniques, Groups & Flattens all validations into notifications
      return [...sportsbookCombinationValidations, ...sportsbookGroupValidations, ...maxPayoutNotification];
    },
  );
